import React, { useState } from 'react';
import {
  Card,
  Image,
  Checkbox,
  Button,
  Tooltip,
  Dropdown,
  Space,
  Tag,
  Typography,
  Empty,
  Spin,
  Modal,
  Form,
  Input,
  message,
} from 'antd';
import {
  DownloadOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  MoreOutlined,
  PlayCircleOutlined,
  AudioOutlined,
  FileOutlined,
} from '@ant-design/icons';
import type { MenuProps } from 'antd';
import { MediaFile } from '../../services/media';
import mediaService from '../../services/media';
import { getFileIconByType } from '../../config/file-type-icons';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface MediaGridViewProps {
  mediaFiles: MediaFile[];
  loading?: boolean;
  selectable?: boolean;
  selectedIds?: number[];
  onSelectionChange?: (selectedIds: number[]) => void;
  onMediaUpdate?: (mediaFile: MediaFile) => void;
  onMediaDelete?: (id: number) => void;
  onMediaSelect?: (mediaFile: MediaFile) => void;
}

// 媒体网格视图组件：在媒体库模态框中展示媒体项，支持选择、预览、编辑、删除
const MediaGridView: React.FC<MediaGridViewProps> = ({
  mediaFiles,
  loading = false,
  selectable = false,
  selectedIds = [],
  onSelectionChange,
  onMediaUpdate,
  onMediaDelete,
  onMediaSelect,
}) => {
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewMedia, setPreviewMedia] = useState<MediaFile | null>(null);
  const [editVisible, setEditVisible] = useState(false);
  const [editingMedia, setEditingMedia] = useState<MediaFile | null>(null);
  const [editForm] = Form.useForm();

  const handleSelection = (mediaId: number, checked: boolean) => {
    if (!onSelectionChange) return;

    if (checked) {
      onSelectionChange([...selectedIds, mediaId]);
    } else {
      onSelectionChange(selectedIds.filter(id => id !== mediaId));
    }
  };

  const handleSelectAll = () => {
    if (!onSelectionChange) return;

    if (selectedIds.length === mediaFiles.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(mediaFiles.map(media => media.id));
    }
  };

  const handlePreview = (media: MediaFile) => {
    setPreviewMedia(media);
    setPreviewVisible(true);
  };

  const handleEdit = (media: MediaFile) => {
    setEditingMedia(media);
    editForm.setFieldsValue({
      title: media.title,
      alt_text: media.alt_text,
      description: media.description,
    });
    setEditVisible(true);
  };

  const handleEditSubmit = async (values: any) => {
    if (!editingMedia) return;

    try {
      const response = await mediaService.updateMedia(editingMedia.id, values);
      if (response.success && response.data) {
        message.success('媒体信息更新成功');
        onMediaUpdate?.(response.data);
        setEditVisible(false);
        setEditingMedia(null);
        editForm.resetFields();
      }
    } catch (error: any) {
      message.error(error.message || '更新失败');
    }
  };

  const handleDelete = (media: MediaFile) => {
    Modal.confirm({
      title: '确认删除',
      content: `确定要删除文件 "${media.title}" 吗？此操作不可恢复。`,
      onOk: () => {
        onMediaDelete?.(media.id);
      },
    });
  };

  const getFileTypeLabel = (media: MediaFile): string => {
    const typeKey = mediaService.getFileTypeIcon(media);
    const map: Record<string, string> = {
      word: 'Word',
      excel: 'Excel',
      ppt: 'PPT',
      pdf: 'PDF',
      zip: 'ZIP',
      rar: 'RAR',
      image: '图片',
      video: '视频',
      audio: '音频',
      document: '文档',
      other: '其他',
    };
    return map[typeKey] || media.file_type || '文件';
  };

  const renderMediaPreview = (media: MediaFile) => {
    const mediaUrl = mediaService.getMediaUrl(media);

    const isSelected = selectedIds.includes(media.id);
    const toggleSelection = () => {
      if (!onSelectionChange) return;
      if (!selectable) {
        // 不可选择时，回退为预览
        handlePreview(media);
        return;
      }
      if (isSelected) {
        onSelectionChange(selectedIds.filter(id => id !== media.id));
      } else {
        onSelectionChange([...selectedIds, media.id]);
      }
    };

    const PreviewIcon = (
      <Button
        type="text"
        icon={<EyeOutlined />}
        onClick={(e) => { e.stopPropagation(); handlePreview(media); }}
        style={{
          position: 'absolute',
          top: 6,
          right: 6,
          zIndex: 2,
          background: 'rgba(255,255,255,0.85)'
        }}
      />
    );

    switch (media.file_type) {
      case 'image':
        return (
          <div
            style={{
              width: '100%',
              height: 120,
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              position: 'relative',
              borderRadius: 8,
              overflow: 'hidden',
            }}
            onClick={toggleSelection}
          >
            {PreviewIcon}
            <Image
              src={mediaUrl}
              alt={media.alt_text || media.title}
              style={{
                width: '100%',
                height: 120,
                objectFit: 'contain',
                cursor: 'pointer',
                display: 'block',
              }}
              preview={false}
              onError={() => console.error('Image load error:', mediaUrl)}
            />
          </div>
        );

      case 'video':
        return (
          <div
            style={{
              width: '100%',
              height: 120,
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              position: 'relative',
              borderRadius: 8,
              overflow: 'hidden',
            }}
            onClick={toggleSelection}
          >
            {PreviewIcon}
            <PlayCircleOutlined style={{ fontSize: 48, color: '#1890ff' }} />
            <div
              style={{
                position: 'absolute',
                bottom: 8,
                right: 8,
                background: 'rgba(0,0,0,0.7)',
                color: 'white',
                padding: '2px 6px',
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              MP4
            </div>
          </div>
        );

      case 'audio':
        return (
          <div
            style={{
              width: '100%',
              height: 120,
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              borderRadius: 8,
              overflow: 'hidden',
            }}
            onClick={toggleSelection}
          >
            {PreviewIcon}
            <AudioOutlined style={{ fontSize: 48, color: '#52c41a' }} />
          </div>
        );

      default:
        return (
          <div
            style={{
              width: '100%',
              height: 120,
              background: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              cursor: 'pointer',
              borderRadius: 8,
              overflow: 'hidden',
            }}
            onClick={toggleSelection}
          >
            {PreviewIcon}
            <div style={{ marginBottom: 4 }}>
              {getFileIconByType(mediaService.getFileTypeIcon(media))}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {getFileTypeLabel(media)}
            </Text>
          </div>
        );
    }
  };

  const getMoreMenuItems = (media: MediaFile): MenuProps['items'] => [
    {
      key: 'preview',
      icon: <EyeOutlined />,
      label: '预览',
      onClick: () => handlePreview(media),
    },
    {
      key: 'download',
      icon: <DownloadOutlined />,
      label: '下载',
      onClick: () => mediaService.downloadMedia(media.id, media.original_name),
    },
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: '编辑信息',
      onClick: () => handleEdit(media),
    },
    {
      type: 'divider',
    },
    {
      key: 'delete',
      icon: <DeleteOutlined />,
      label: '删除',
      danger: true,
      onClick: () => handleDelete(media),
    },
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (mediaFiles.length === 0) {
    return (
      <Empty
        description="暂无媒体文件"
        image={Empty.PRESENTED_IMAGE_SIMPLE}
      />
    );
  }

  return (
    <>
      {/* 选择工具栏 */}
      {selectable && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type={selectedIds.length === mediaFiles.length ? 'primary' : 'default'}
              onClick={handleSelectAll}
            >
              {selectedIds.length === mediaFiles.length ? '取消全选' : '全选'}
            </Button>
            {selectedIds.length > 0 && (
              <Text>已选择 {selectedIds.length} 个文件</Text>
            )}
          </Space>
        </div>
      )}

      {/* 媒体网格 */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
          gap: 12,
        }}
      >
        {mediaFiles.map((media) => (
          <Card
            key={media.id}
            hoverable
            styles={{ body: { padding: 0 } }}
            style={{
              position: 'relative',
              border: selectedIds.includes(media.id) ? '2px solid #1890ff' : undefined,
            }}
            onClick={() => onMediaSelect?.(media)}
          >
            {/* 选择框 */}
            {selectable && (
              <Checkbox
                checked={selectedIds.includes(media.id)}
                onChange={(e) => {
                  e.stopPropagation();
                  handleSelection(media.id, e.target.checked);
                }}
                style={{
                  position: 'absolute',
                  top: 8,
                  left: 8,
                  zIndex: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                  borderRadius: 4,
                  padding: '2px 4px',
                }}
              />
            )}

            {/* 更多操作菜单 */}
            <Dropdown
              menu={{ items: getMoreMenuItems(media) }}
              trigger={['click']}
            >
              <Button
                type="text"
                icon={<MoreOutlined />}
                style={{
                  position: 'absolute',
                  top: 8,
                  right: 8,
                  zIndex: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.8)',
                }}
                onClick={(e) => e.stopPropagation()}
              />
            </Dropdown>

            {/* 媒体预览 */}
            {renderMediaPreview(media)}

            {/* 媒体信息 */}
            <div style={{ padding: 8 }}>
              <Tooltip title={media.title}>
                <Text strong ellipsis style={{ display: 'block' }}>
                  {media.title}
                </Text>
              </Tooltip>

              <div style={{ marginTop: 8 }}>
                <Tag color="blue" style={{ fontSize: 11, lineHeight: '16px' }}>
                  {getFileTypeLabel(media)}
                </Tag>
              </div>

              {media.description && (
                <Paragraph
                  ellipsis={{ rows: 2, expandable: false }}
                  style={{ fontSize: 12, color: '#666', marginTop: 8, marginBottom: 0 }}
                >
                  {media.description}
                </Paragraph>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* 预览模态框 */}
      <Modal
        title="媒体预览"
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        footer={
          <Space>
            <Button onClick={() => setPreviewVisible(false)}>关闭</Button>
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              onClick={() => previewMedia && mediaService.downloadMedia(previewMedia.id, previewMedia.original_name)}
            >
              下载文件
            </Button>
          </Space>
        }
        width="600px"
        centered
      >
        {previewMedia && (
          <div>
            <div style={{ textAlign: 'center', marginBottom: 16 }}>
              {previewMedia.file_type === 'image' ? (
                <Image
                  src={mediaService.getMediaUrl(previewMedia)}
                  alt={previewMedia.alt_text || previewMedia.title}
                  style={{ maxWidth: '100%', maxHeight: '60vh' }}
                />
              ) : previewMedia.file_type === 'video' ? (
                <video
                  src={mediaService.getMediaUrl(previewMedia)}
                  controls
                  style={{ maxWidth: '100%', maxHeight: '60vh' }}
                />
              ) : previewMedia.file_type === 'audio' ? (
                <audio
                  src={mediaService.getMediaUrl(previewMedia)}
                  controls
                  style={{ width: '100%' }}
                />
              ) : (
                <div style={{ padding: 48, textAlign: 'center' }}>
                  <FileOutlined style={{ fontSize: 64, color: '#666' }} />
                  <div style={{ marginTop: 16 }}>
                    <Text>无法预览此文件类型</Text>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div><strong>文件名:</strong> {previewMedia.title}</div>
                <div><strong>原始名称:</strong> {previewMedia.original_name}</div>
                <div><strong>文件类型:</strong> {previewMedia.mime_type}</div>
                <div><strong>文件大小:</strong> {mediaService.formatFileSize(previewMedia.file_size)}</div>
                {previewMedia.dimensions && (
                  <div><strong>尺寸:</strong> {previewMedia.dimensions.width} × {previewMedia.dimensions.height}</div>
                )}
                <div><strong>上传时间:</strong> {new Date(previewMedia.upload_date).toLocaleString()}</div>
                {previewMedia.alt_text && (
                  <div><strong>替代文本:</strong> {previewMedia.alt_text}</div>
                )}
                {previewMedia.description && (
                  <div><strong>描述:</strong> {previewMedia.description}</div>
                )}
              </Space>
            </div>
          </div>
        )}
      </Modal>

      {/* 编辑模态框 */}
      <Modal
        title="编辑媒体信息"
        open={editVisible}
        onCancel={() => {
          setEditVisible(false);
          setEditingMedia(null);
          editForm.resetFields();
        }}
        onOk={() => editForm.submit()}
        confirmLoading={false}
      >
        <Form
          form={editForm}
          layout="vertical"
          onFinish={handleEditSubmit}
        >
          <Form.Item
            label="标题"
            name="title"
            rules={[{ required: true, message: '请输入标题' }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            label="替代文本"
            name="alt_text"
          >
            <Input placeholder="用于图片的替代文本" />
          </Form.Item>

          <Form.Item
            label="描述"
            name="description"
          >
            <TextArea rows={3} placeholder="文件描述" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default MediaGridView;
